import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { professorAPI } from '../lib/api';
import { SUBJECTS, ORGAN_SYSTEMS, cn } from '../lib/utils';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import {
    Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../components/ui/table';
import { toast } from 'sonner';
import {
    Plus, Copy, Check, Trash2, Eye, BarChart3, Loader2, ClipboardList, Users, Award,
    GraduationCap, Lock, Unlock, BookOpen, Wand2, Share2, QrCode, Link2, ExternalLink,
    Target, Send, Zap
} from 'lucide-react';
import { QRCodeSVG } from 'qrcode.react';

export default function ProfessorDashboard() {
    const { user } = useAuth();
    const { isDark } = useTheme();
    const navigate = useNavigate();
    const [quizzes, setQuizzes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [copiedCode, setCopiedCode] = useState(null);

    // Create quiz state
    const [showCreate, setShowCreate] = useState(false);
    const [creating, setCreating] = useState(false);
    const [quizForm, setQuizForm] = useState({
        title: '', subjects: [], organ_systems: [], description: '', num_questions: 10,
        time_limit_minutes: 30, difficulties: [], mode: 'ai'
    });

    // Manual questions state
    const [manualQuestions, setManualQuestions] = useState([{
        question_stem: '', vignette: '', correct_answer: 'A', explanation: '',
        options: [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }, { letter: 'D', text: '' }]
    }]);

    // Results state
    const [resultsDialog, setResultsDialog] = useState({ open: false, quizId: null });
    const [results, setResults] = useState(null);
    const [loadingResults, setLoadingResults] = useState(false);

    // Share state
    const [shareQuiz, setShareQuiz] = useState(null);
    const [copiedLink, setCopiedLink] = useState(false);

    // Assigned plans state
    const [assignedPlans, setAssignedPlans] = useState([]);
    const [students, setStudents] = useState([]);
    const [showAssign, setShowAssign] = useState(false);
    const [assigning, setAssigning] = useState(false);
    const [autoAssigning, setAutoAssigning] = useState(null);
    const [planStats, setPlanStats] = useState([]);
    const [loadingStats, setLoadingStats] = useState(false);
    const [assignForm, setAssignForm] = useState({
        student_emails: [], name: '', subjects: [], organ_systems: [], specific_topics: '', target_score: 70,
    });

    useEffect(() => {
        if (user && !user.is_professor && !user.is_admin) {
            toast.error('Professor access required');
            navigate('/dashboard');
            return;
        }
        fetchQuizzes();
        fetchStudents();
        fetchAssignedPlans();
        fetchPlanStats();
    }, [user, navigate]);

    const fetchQuizzes = async () => {
        try {
            setLoading(true);
            const res = await professorAPI.getQuizzes();
            setQuizzes(res.data.quizzes || []);
        } catch {
            toast.error('Failed to load quizzes');
        } finally {
            setLoading(false);
        }
    };

    const fetchStudents = async () => {
        try {
            const res = await professorAPI.getStudents();
            setStudents(res.data.students || []);
        } catch { /* ignore */ }
    };

    const fetchAssignedPlans = async () => {
        try {
            const res = await professorAPI.getAssignedPlans();
            setAssignedPlans(res.data.plans || []);
        } catch { /* ignore */ }
    };

    const fetchPlanStats = async () => {
        setLoadingStats(true);
        try {
            const res = await professorAPI.getAssignedPlanStats();
            setPlanStats(res.data.stats || []);
        } catch { /* ignore */ }
        finally { setLoadingStats(false); }
    };

    const copyCode = (code) => {
        navigator.clipboard.writeText(code);
        setCopiedCode(code);
        setTimeout(() => setCopiedCode(null), 2000);
        toast.success('Quiz code copied');
    };

    const getQuizUrl = (code) => `${window.location.origin}/quiz?code=${code}`;

    const copyQuizLink = (code) => {
        navigator.clipboard.writeText(getQuizUrl(code));
        setCopiedLink(true);
        setTimeout(() => setCopiedLink(false), 2000);
        toast.success('Quiz link copied');
    };

    const openShareDialog = (quiz) => {
        setShareQuiz(quiz);
        setCopiedLink(false);
    };

    const [generatingQuizId, setGeneratingQuizId] = useState(null);
    const [genProgress, setGenProgress] = useState({ ready: 0, total: 0 });

    const handleCreateQuiz = async () => {
        if (!quizForm.title.trim()) { toast.error('Enter a quiz title'); return; }
        if (quizForm.subjects.length === 0) { toast.error('Select at least one subject'); return; }
        if (quizForm.difficulties.length === 0) { toast.error('Select at least one difficulty'); return; }
        setCreating(true);
        try {
            const payload = {
                ...quizForm,
                subject: quizForm.subjects[0],
                difficulty: quizForm.difficulties[0],
            };
            if (quizForm.mode === 'manual') {
                const valid = manualQuestions.every(q => q.question_stem && q.options.every(o => o.text));
                if (!valid) { toast.error('Fill in all question fields'); setCreating(false); return; }
                payload.questions = manualQuestions;
                payload.num_questions = manualQuestions.length;
            }
            const res = await professorAPI.createQuiz(payload);

            if (res.data.status === 'generating') {
                // AI quiz — poll for progress
                setShowCreate(false);
                setGeneratingQuizId(res.data.id);
                setGenProgress({ ready: 0, total: quizForm.num_questions });
                toast.info(`Generating ${quizForm.num_questions} questions... This will take a moment.`);
                resetForm();

                const poll = setInterval(async () => {
                    try {
                        const pr = await professorAPI.getQuizProgress(res.data.id);
                        setGenProgress({ ready: pr.data.questions_ready, total: pr.data.num_questions });
                        if (pr.data.status === 'active' || pr.data.status === 'failed') {
                            clearInterval(poll);
                            setGeneratingQuizId(null);
                            if (pr.data.status === 'active') {
                                toast.success(`Quiz ready! ${pr.data.questions_ready} questions generated. Code: ${res.data.quiz_code}`);
                            } else {
                                toast.error('Quiz generation failed');
                            }
                            fetchQuizzes();
                        }
                    } catch {
                        // ignore polling errors
                    }
                }, 2500);
            } else {
                toast.success(`Quiz created! Code: ${res.data.quiz_code}`);
                setShowCreate(false);
                resetForm();
                fetchQuizzes();
            }
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to create quiz');
        } finally {
            setCreating(false);
        }
    };

    const resetForm = () => {
        setQuizForm({ title: '', subjects: [], organ_systems: [], description: '', num_questions: 10, time_limit_minutes: 30, difficulties: [], mode: 'ai' });
        setManualQuestions([{ question_stem: '', vignette: '', correct_answer: 'A', explanation: '',
            options: [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }, { letter: 'D', text: '' }] }]);
    };

    const handleToggleStatus = async (quiz) => {
        const newStatus = quiz.status === 'active' ? 'closed' : 'active';
        try {
            await professorAPI.updateQuizStatus(quiz.id, newStatus);
            toast.success(`Quiz ${newStatus === 'active' ? 'activated' : 'closed'}`);
            fetchQuizzes();
        } catch { toast.error('Failed to update'); }
    };

    const handleDelete = async (quizId) => {
        try {
            await professorAPI.deleteQuiz(quizId);
            toast.success('Quiz deleted');
            fetchQuizzes();
        } catch { toast.error('Failed to delete'); }
    };

    const viewResults = async (quizId) => {
        setResultsDialog({ open: true, quizId });
        setLoadingResults(true);
        try {
            const res = await professorAPI.getQuizResults(quizId);
            setResults(res.data);
        } catch {
            toast.error('Failed to load results');
        } finally {
            setLoadingResults(false);
        }
    };

    const addManualQuestion = () => {
        setManualQuestions(prev => [...prev, {
            question_stem: '', vignette: '', correct_answer: 'A', explanation: '',
            options: [{ letter: 'A', text: '' }, { letter: 'B', text: '' }, { letter: 'C', text: '' }, { letter: 'D', text: '' }]
        }]);
    };

    const updateManualQuestion = (idx, field, value) => {
        setManualQuestions(prev => {
            const next = [...prev];
            next[idx] = { ...next[idx], [field]: value };
            return next;
        });
    };

    const updateManualOption = (qIdx, oIdx, text) => {
        setManualQuestions(prev => {
            const next = [...prev];
            const opts = [...next[qIdx].options];
            opts[oIdx] = { ...opts[oIdx], text };
            next[qIdx] = { ...next[qIdx], options: opts };
            return next;
        });
    };

    const removeManualQuestion = (idx) => {
        setManualQuestions(prev => prev.filter((_, i) => i !== idx));
    };

    const handleAssignPlan = async () => {
        if (assignForm.student_emails.length === 0) { toast.error('Select at least one student'); return; }
        if (!assignForm.name.trim()) { toast.error('Enter a plan name'); return; }
        if (assignForm.subjects.length === 0) { toast.error('Select at least one subject'); return; }
        setAssigning(true);
        try {
            const res = await professorAPI.assignPlan(assignForm);
            toast.success(`Assigned plan to ${res.data.count} student(s)`);
            setShowAssign(false);
            setAssignForm({ student_emails: [], name: '', subjects: [], organ_systems: [], specific_topics: '', target_score: 70 });
            fetchAssignedPlans();
            fetchPlanStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to assign plan');
        } finally {
            setAssigning(false);
        }
    };

    const handleAutoAssign = async (quizId) => {
        setAutoAssigning(quizId);
        try {
            const res = await professorAPI.autoAssign(quizId);
            if (res.data.count === 0) {
                toast.info('No weak areas found — all students scored above 70%');
            } else {
                toast.success(`Created ${res.data.count} study plan(s) for weak areas`);
            }
            fetchAssignedPlans();
            fetchPlanStats();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to auto-assign');
        } finally {
            setAutoAssigning(null);
        }
    };

    const handleDeleteAssignedPlan = async (planId) => {
        try {
            await professorAPI.deleteAssignedPlan(planId);
            toast.success('Assigned plan removed');
            setAssignedPlans(prev => prev.filter(p => p.id !== planId));
        } catch {
            toast.error('Failed to delete plan');
        }
    };

    const toggleAssignEmail = (email) => {
        setAssignForm(prev => ({
            ...prev,
            student_emails: prev.student_emails.includes(email)
                ? prev.student_emails.filter(e => e !== email)
                : [...prev.student_emails, email],
        }));
    };

    const toggleAssignSubject = (subject) => {
        setAssignForm(prev => ({
            ...prev,
            subjects: prev.subjects.includes(subject) ? prev.subjects.filter(s => s !== subject) : [...prev.subjects, subject],
        }));
    };

    const toggleAssignSystem = (system) => {
        setAssignForm(prev => ({
            ...prev,
            organ_systems: prev.organ_systems.includes(system) ? prev.organ_systems.filter(s => s !== system) : [...prev.organ_systems, system],
        }));
    };

    if (!user?.is_professor && !user?.is_admin) return null;

    return (
        <Layout>
            <div className="space-y-6 animate-fade-in" data-testid="professor-dashboard">
                {/* Header */}
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                    <div>
                        <h1 className={cn("text-2xl md:text-3xl font-bold", isDark ? "text-white" : "text-slate-900")} style={{ fontFamily: 'Manrope' }}>
                            Professor Dashboard
                        </h1>
                        <p className={isDark ? "text-slate-400 mt-1" : "text-slate-500 mt-1"}>Create and manage quizzes for your students</p>
                    </div>
                    <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="professor-create-quiz">
                        <Plus className="mr-2 h-4 w-4" />Create Quiz
                    </Button>
                </div>

                {/* Stats */}
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                    <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total Quizzes</p>
                                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")}>{quizzes.length}</p>
                            </div>
                            <ClipboardList className="h-8 w-8 text-indigo-500" />
                        </CardContent>
                    </Card>
                    <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Active</p>
                                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")}>{quizzes.filter(q => q.status === 'active').length}</p>
                            </div>
                            <Unlock className="h-8 w-8 text-emerald-500" />
                        </CardContent>
                    </Card>
                    <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                        <CardContent className="p-4 flex items-center justify-between">
                            <div>
                                <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>Total Responses</p>
                                <p className={cn("text-2xl font-bold", isDark ? "text-white" : "text-slate-900")}>{quizzes.reduce((s, q) => s + (q.total_attempts || 0), 0)}</p>
                            </div>
                            <Users className="h-8 w-8 text-amber-500" />
                        </CardContent>
                    </Card>
                </div>

                {/* Main Tabs */}
                <Tabs defaultValue="quizzes" className="w-full">
                    <TabsList className="grid w-full grid-cols-2 max-w-md">
                        <TabsTrigger value="quizzes" className="flex items-center gap-2">
                            <ClipboardList className="h-4 w-4" />
                            Quizzes
                        </TabsTrigger>
                        <TabsTrigger value="assigned" className="flex items-center gap-2">
                            <Target className="h-4 w-4" />
                            Assigned Plans
                            {assignedPlans.length > 0 && <Badge className="ml-1 bg-red-100 text-red-700 text-xs">{assignedPlans.length}</Badge>}
                        </TabsTrigger>
                    </TabsList>

                    <TabsContent value="quizzes" className="mt-4 space-y-4">
                {/* Generation Progress Banner */}
                {generatingQuizId && (
                    <Card className={cn("border-2 border-amber-400", isDark ? "bg-amber-900/20" : "bg-amber-50")} data-testid="quiz-generating-banner">
                        <CardContent className="p-4 flex items-center gap-3">
                            <Loader2 className="h-5 w-5 animate-spin text-amber-500 shrink-0" />
                            <div className="flex-1">
                                <p className={cn("text-sm font-medium", isDark ? "text-amber-200" : "text-amber-800")}>
                                    Generating quiz questions... {genProgress.ready}/{genProgress.total} ready
                                </p>
                                <div className="mt-1.5 h-1.5 rounded-full bg-amber-200 overflow-hidden">
                                    <div className="h-full bg-amber-500 rounded-full transition-all duration-500" style={{ width: `${genProgress.total > 0 ? (genProgress.ready / genProgress.total) * 100 : 0}%` }} />
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                )}
                {/* Quiz List */}
                <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                    <CardHeader>
                        <CardTitle className={isDark ? "text-white" : ""} style={{ fontFamily: 'Manrope' }}>Your Quizzes</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {loading ? (
                            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                        ) : quizzes.length === 0 ? (
                            <div className={cn("text-center py-12", isDark ? "text-slate-400" : "text-slate-500")}>
                                <GraduationCap className="h-12 w-12 mx-auto mb-3 opacity-40" />
                                <p>No quizzes yet. Create your first quiz to get started.</p>
                            </div>
                        ) : (
                            <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                                <Table>
                                    <TableHeader>
                                        <TableRow className={isDark ? "bg-slate-700/50" : "bg-slate-50"}>
                                            <TableHead className={isDark ? "text-slate-300" : ""}>Quiz</TableHead>
                                            <TableHead className={isDark ? "text-slate-300" : ""}>Code</TableHead>
                                            <TableHead className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>Subject</TableHead>
                                            <TableHead className={cn("hidden md:table-cell", isDark ? "text-slate-300" : "")}>Questions</TableHead>
                                            <TableHead className={isDark ? "text-slate-300" : ""}>Responses</TableHead>
                                            <TableHead className={isDark ? "text-slate-300" : ""}>Status</TableHead>
                                            <TableHead className={cn("text-right", isDark ? "text-slate-300" : "")}>Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {quizzes.map(q => (
                                            <TableRow key={q.id} className={isDark ? "border-slate-700" : ""}>
                                                <TableCell>
                                                    <p className={cn("font-medium", isDark ? "text-slate-200" : "text-slate-900")}>{q.title}</p>
                                                    <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>{q.difficulty} &middot; {q.time_limit_minutes}min</p>
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex items-center gap-1">
                                                        <code className={cn("px-2 py-1 rounded text-sm font-mono", isDark ? "bg-slate-700 text-indigo-300" : "bg-slate-100 text-indigo-700")}>{q.quiz_code}</code>
                                                        <button onClick={() => copyCode(q.quiz_code)} className="p-1">
                                                            {copiedCode === q.quiz_code ? <Check className="h-3 w-3 text-emerald-500" /> : <Copy className="h-3 w-3 text-slate-400" />}
                                                        </button>
                                                    </div>
                                                </TableCell>
                                                <TableCell className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>{q.subject}</TableCell>
                                                <TableCell className={cn("hidden md:table-cell", isDark ? "text-slate-300" : "")}>{q.num_questions}</TableCell>
                                                <TableCell className={isDark ? "text-slate-300" : ""}>{q.total_attempts || 0}</TableCell>
                                                <TableCell>
                                                    {q.status === 'generating' ? (
                                                        <Badge className="bg-amber-100 text-amber-700" data-testid={`quiz-status-${q.id}`}><Loader2 className="h-3 w-3 mr-1 animate-spin" />Generating</Badge>
                                                    ) : q.status === 'failed' ? (
                                                        <Badge className="bg-red-100 text-red-700" data-testid={`quiz-status-${q.id}`}>Failed</Badge>
                                                    ) : q.status === 'active' ? (
                                                        <Badge className="bg-emerald-100 text-emerald-700" data-testid={`quiz-status-${q.id}`}><Unlock className="h-3 w-3 mr-1" />Active</Badge>
                                                    ) : (
                                                        <Badge className="bg-slate-100 text-slate-600" data-testid={`quiz-status-${q.id}`}><Lock className="h-3 w-3 mr-1" />Closed</Badge>
                                                    )}
                                                </TableCell>
                                                <TableCell>
                                                    <div className="flex gap-1 justify-end">
                                                        <Button variant="ghost" size="sm" onClick={() => openShareDialog(q)} data-testid={`quiz-share-${q.id}`} title="Share quiz">
                                                            <Share2 className="h-4 w-4 text-indigo-500" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => viewResults(q.id)} data-testid={`quiz-results-${q.id}`}>
                                                            <BarChart3 className="h-4 w-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => handleToggleStatus(q)} data-testid={`quiz-toggle-${q.id}`}>
                                                            {q.status === 'active' ? <Lock className="h-4 w-4" /> : <Unlock className="h-4 w-4" />}
                                                        </Button>
                                                        <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDelete(q.id)} data-testid={`quiz-delete-${q.id}`}>
                                                            <Trash2 className="h-4 w-4" />
                                                        </Button>
                                                    </div>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
                    </TabsContent>

                    <TabsContent value="assigned" className="mt-4 space-y-4">
                        <div className="flex justify-end">
                            <Button onClick={() => setShowAssign(true)} className="bg-red-600 hover:bg-red-700 text-white" data-testid="assign-plan-btn">
                                <Send className="mr-2 h-4 w-4" />Assign Study Plan
                            </Button>
                        </div>

                        {assignedPlans.length === 0 ? (
                            <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                                <CardContent className="py-12 text-center">
                                    <Target className={cn("h-12 w-12 mx-auto mb-3 opacity-40", isDark ? "text-slate-500" : "text-slate-300")} />
                                    <p className={isDark ? "text-slate-400" : "text-slate-500"}>No study plans assigned yet. Assign plans manually or auto-assign from quiz results.</p>
                                </CardContent>
                            </Card>
                        ) : (
                            <Card className={cn("border", isDark ? "bg-slate-800 border-slate-700" : "border-slate-200")}>
                                <CardHeader>
                                    <CardTitle className={isDark ? "text-white" : ""} style={{ fontFamily: 'Manrope' }}>Assigned Study Plans</CardTitle>
                                </CardHeader>
                                <CardContent>
                                    {loadingStats ? (
                                        <div className="flex items-center justify-center py-8">
                                            <Loader2 className="h-5 w-5 animate-spin text-slate-400 mr-2" />
                                            <span className={isDark ? "text-slate-400" : "text-slate-500"}>Loading stats...</span>
                                        </div>
                                    ) : (
                                    <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                                        <Table>
                                            <TableHeader>
                                                <TableRow className={isDark ? "bg-slate-700/50" : "bg-slate-50"}>
                                                    <TableHead className={isDark ? "text-slate-300" : ""}>Plan</TableHead>
                                                    <TableHead className={isDark ? "text-slate-300" : ""}>Student</TableHead>
                                                    <TableHead className={cn("hidden md:table-cell", isDark ? "text-slate-300" : "")}>Sessions</TableHead>
                                                    <TableHead className={cn("hidden md:table-cell", isDark ? "text-slate-300" : "")}>Questions</TableHead>
                                                    <TableHead className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>Accuracy</TableHead>
                                                    <TableHead className={cn("hidden lg:table-cell", isDark ? "text-slate-300" : "")}>Last Active</TableHead>
                                                    <TableHead className={cn("text-right", isDark ? "text-slate-300" : "")}>Actions</TableHead>
                                                </TableRow>
                                            </TableHeader>
                                            <TableBody>
                                                {assignedPlans.map(p => {
                                                    const stat = planStats.find(s => s.plan_id === p.id) || {};
                                                    const acc = stat.accuracy || 0;
                                                    const accColor = acc >= 70 ? "text-emerald-600" : acc >= 50 ? "text-amber-600" : "text-red-600";
                                                    return (
                                                    <TableRow key={p.id} className={isDark ? "border-slate-700" : ""} data-testid={`assigned-row-${p.id}`}>
                                                        <TableCell>
                                                            <p className={cn("font-medium", isDark ? "text-slate-200" : "text-slate-900")}>{p.name}</p>
                                                            <div className="flex gap-1 mt-0.5">
                                                                {p.subjects?.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                                                                <Badge className={p.source === 'auto_from_quiz' ? "bg-amber-100 text-amber-700 text-xs" : "bg-indigo-100 text-indigo-700 text-xs"}>
                                                                    {p.source === 'auto_from_quiz' ? 'Auto' : 'Manual'}
                                                                </Badge>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className={cn("text-sm", isDark ? "text-slate-300" : "")}>{p.student_email}</TableCell>
                                                        <TableCell className={cn("hidden md:table-cell text-center", isDark ? "text-slate-300" : "")}>
                                                            {stat.sessions_count > 0 ? (
                                                                <div>
                                                                    <span className="font-medium">{stat.sessions_completed}</span>
                                                                    <span className={isDark ? "text-slate-500" : "text-slate-400"}>/{stat.sessions_count}</span>
                                                                </div>
                                                            ) : (
                                                                <span className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>Not started</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className={cn("hidden md:table-cell text-center", isDark ? "text-slate-300" : "")}>
                                                            {stat.total_questions > 0 ? (
                                                                <div>
                                                                    <span className="font-medium">{stat.correct_answers}</span>
                                                                    <span className={isDark ? "text-slate-500" : "text-slate-400"}>/{stat.total_questions}</span>
                                                                </div>
                                                            ) : (
                                                                <span className={isDark ? "text-slate-500" : "text-slate-400"}>—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="hidden sm:table-cell text-center">
                                                            {stat.total_questions > 0 ? (
                                                                <span className={cn("font-bold", accColor)}>{acc}%</span>
                                                            ) : (
                                                                <span className={isDark ? "text-slate-500" : "text-slate-400"}>—</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className={cn("hidden lg:table-cell text-sm", isDark ? "text-slate-400" : "text-slate-500")}>
                                                            {stat.last_activity ? new Date(stat.last_activity).toLocaleDateString() : '—'}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button variant="ghost" size="sm" className="text-red-500" onClick={() => handleDeleteAssignedPlan(p.id)} data-testid={`delete-assigned-${p.id}`}>
                                                                <Trash2 className="h-4 w-4" />
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                    );
                                                })}
                                            </TableBody>
                                        </Table>
                                    </div>
                                    )}
                                </CardContent>
                            </Card>
                        )}
                    </TabsContent>
                </Tabs>

                {/* Assign Plan Dialog */}
                <Dialog open={showAssign} onOpenChange={(o) => { if (!o) setShowAssign(false); }}>
                    <DialogContent className={cn("sm:max-w-2xl max-h-[90vh] overflow-y-auto", isDark && "bg-slate-900 border-slate-700 text-slate-100")} data-testid="assign-plan-dialog">
                        <DialogHeader>
                            <DialogTitle className={cn("flex items-center gap-2", isDark && "text-white")}>
                                <Send className="h-5 w-5 text-red-500" />Assign Study Plan
                            </DialogTitle>
                            <DialogDescription className={isDark ? "text-slate-400" : ""}>Create a study plan and assign it to students who've taken your quizzes.</DialogDescription>
                        </DialogHeader>
                        <div className="space-y-4 py-2">
                            <div>
                                <Label className="mb-1.5 block">Select Students</Label>
                                {students.length === 0 ? (
                                    <p className={cn("text-sm", isDark ? "text-slate-400" : "text-slate-500")}>No students found. Students appear here after taking your quizzes.</p>
                                ) : (
                                    <div className="flex flex-wrap gap-1.5 max-h-[150px] overflow-y-auto" data-testid="assign-students-list">
                                        {students.map(s => {
                                            const sel = assignForm.student_emails.includes(s.email);
                                            return (
                                                <button key={s.email} onClick={() => toggleAssignEmail(s.email)}
                                                    className={cn("px-2.5 py-1.5 rounded-lg border text-xs font-medium transition-all",
                                                        sel ? "bg-red-50 border-red-300 text-red-700" : isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
                                                    )}>
                                                    {s.name} ({s.email}) — Avg: {s.avg_score}%
                                                </button>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                            <div>
                                <Label>Plan Name</Label>
                                <Input value={assignForm.name} onChange={e => setAssignForm(f => ({ ...f, name: e.target.value }))} placeholder="e.g. Cardiology Review" className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} data-testid="assign-plan-name" />
                            </div>
                            <div>
                                <Label className="mb-1.5 block">Subjects</Label>
                                <div className="flex flex-wrap gap-1.5">
                                    {SUBJECTS.map(s => {
                                        const sel = assignForm.subjects.includes(s);
                                        return (
                                            <button key={s} onClick={() => toggleAssignSubject(s)}
                                                className={cn("px-2.5 py-1 rounded-full border text-xs font-medium transition-all",
                                                    sel ? "bg-red-50 border-red-300 text-red-700" : isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
                                                )}>{s}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label className="mb-1.5 block">Organ Systems (optional)</Label>
                                <div className="flex flex-wrap gap-1.5">
                                    {ORGAN_SYSTEMS.map(s => {
                                        const sel = assignForm.organ_systems.includes(s);
                                        return (
                                            <button key={s} onClick={() => toggleAssignSystem(s)}
                                                className={cn("px-2.5 py-1 rounded-full border text-xs font-medium transition-all",
                                                    sel ? "bg-red-50 border-red-300 text-red-700" : isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
                                                )}>{s}</button>
                                        );
                                    })}
                                </div>
                            </div>
                            <div>
                                <Label>Specific Topics (optional)</Label>
                                <Input value={assignForm.specific_topics} onChange={e => setAssignForm(f => ({ ...f, specific_topics: e.target.value }))} placeholder="e.g. Cardiac arrhythmias, Renal tubular acidosis" className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} />
                            </div>
                            <div>
                                <Label>Target Score (%)</Label>
                                <Input type="number" min={30} max={100} value={assignForm.target_score} onChange={e => setAssignForm(f => ({ ...f, target_score: Math.min(100, Math.max(30, parseInt(e.target.value) || 70)) }))} className={cn("w-24", isDark ? "bg-slate-800 border-slate-600 text-white" : "")} />
                            </div>
                        </div>
                        <DialogFooter>
                            <Button variant="outline" onClick={() => setShowAssign(false)}>Cancel</Button>
                            <Button onClick={handleAssignPlan} disabled={assigning} className="bg-red-600 hover:bg-red-700" data-testid="confirm-assign-btn">
                                {assigning ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Assigning...</> : <><Send className="h-4 w-4 mr-2" />Assign Plan</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Create Quiz Dialog */}
                <Dialog open={showCreate} onOpenChange={(o) => { if (!o) { setShowCreate(false); resetForm(); } }}>
                    <DialogContent className={cn("sm:max-w-2xl max-h-[90vh] overflow-y-auto", isDark && "bg-slate-900 border-slate-700 text-slate-100")} data-testid="create-quiz-dialog">
                        <DialogHeader>
                            <DialogTitle className={cn("flex items-center gap-2", isDark && "text-white")}>
                                <ClipboardList className="h-5 w-5 text-indigo-500" />Create New Quiz
                            </DialogTitle>
                            <DialogDescription className={isDark ? "text-slate-400" : ""}>Choose to auto-generate questions with AI or create them manually.</DialogDescription>
                        </DialogHeader>

                        <div className="space-y-4 py-2">
                            {/* Mode selector */}
                            <div className="flex gap-2" data-testid="quiz-mode-selector">
                                <button onClick={() => setQuizForm(f => ({ ...f, mode: 'ai' }))}
                                    className={cn("flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition-all",
                                        quizForm.mode === 'ai' ? isDark ? "bg-indigo-900/50 border-indigo-500 text-indigo-300" : "bg-indigo-50 border-indigo-300 text-indigo-700"
                                        : isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
                                    )} data-testid="quiz-mode-ai">
                                    <Wand2 className="h-4 w-4" />AI Generated
                                </button>
                                <button onClick={() => setQuizForm(f => ({ ...f, mode: 'manual' }))}
                                    className={cn("flex-1 flex items-center justify-center gap-2 px-3 py-3 rounded-lg border text-sm font-medium transition-all",
                                        quizForm.mode === 'manual' ? isDark ? "bg-indigo-900/50 border-indigo-500 text-indigo-300" : "bg-indigo-50 border-indigo-300 text-indigo-700"
                                        : isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-white border-slate-200 text-slate-600"
                                    )} data-testid="quiz-mode-manual">
                                    <BookOpen className="h-4 w-4" />Manual
                                </button>
                            </div>

                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                <div><Label>Title</Label><Input value={quizForm.title} onChange={e => setQuizForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Midterm Review" data-testid="quiz-title-input" className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} /></div>
                                <div><Label>Description (optional)</Label><Input value={quizForm.description} onChange={e => setQuizForm(f => ({ ...f, description: e.target.value }))} placeholder="Brief description" className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} /></div>
                            </div>

                            {/* Subjects multi-select */}
                            <div>
                                <Label className="mb-1.5 block">Subjects</Label>
                                <div className="flex flex-wrap gap-1.5" data-testid="quiz-subjects-select">
                                    {SUBJECTS.map(s => {
                                        const sel = quizForm.subjects.includes(s);
                                        return (
                                            <button key={s} type="button"
                                                onClick={() => setQuizForm(f => ({ ...f, subjects: sel ? f.subjects.filter(x => x !== s) : [...f.subjects, s] }))}
                                                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-all",
                                                    sel ? (isDark ? "bg-indigo-900/60 border-indigo-500 text-indigo-200" : "bg-indigo-100 border-indigo-300 text-indigo-800")
                                                        : (isDark ? "bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300")
                                                )}>
                                                {s}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Organ Systems multi-select */}
                            <div>
                                <Label className="mb-1.5 block">Organ Systems (optional)</Label>
                                <div className="flex flex-wrap gap-1.5" data-testid="quiz-organ-systems-select">
                                    {ORGAN_SYSTEMS.map(os => {
                                        const sel = quizForm.organ_systems.includes(os);
                                        return (
                                            <button key={os} type="button"
                                                onClick={() => setQuizForm(f => ({ ...f, organ_systems: sel ? f.organ_systems.filter(x => x !== os) : [...f.organ_systems, os] }))}
                                                className={cn("px-2.5 py-1 rounded-full text-xs font-medium border transition-all",
                                                    sel ? (isDark ? "bg-purple-900/60 border-purple-500 text-purple-200" : "bg-purple-100 border-purple-300 text-purple-800")
                                                        : (isDark ? "bg-slate-800 border-slate-600 text-slate-400 hover:border-slate-500" : "bg-white border-slate-200 text-slate-500 hover:border-slate-300")
                                                )}>
                                                {os}
                                            </button>
                                        );
                                    })}
                                </div>
                            </div>

                            {/* Difficulty multi-select + settings row */}
                            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                                <div className="sm:col-span-1">
                                    <Label className="mb-1.5 block">Difficulty</Label>
                                    <div className="flex gap-1.5" data-testid="quiz-difficulty-select">
                                        {['easy', 'medium', 'hard'].map(d => {
                                            const sel = quizForm.difficulties.includes(d);
                                            const colors = { easy: 'emerald', medium: 'amber', hard: 'red' };
                                            const c = colors[d];
                                            return (
                                                <button key={d} type="button"
                                                    onClick={() => setQuizForm(f => ({ ...f, difficulties: sel ? f.difficulties.filter(x => x !== d) : [...f.difficulties, d] }))}
                                                    className={cn("flex-1 px-2.5 py-1.5 rounded-lg text-xs font-medium border transition-all capitalize",
                                                        sel ? (isDark ? `bg-${c}-900/60 border-${c}-500 text-${c}-200` : `bg-${c}-100 border-${c}-300 text-${c}-800`)
                                                            : (isDark ? "bg-slate-800 border-slate-600 text-slate-400" : "bg-white border-slate-200 text-slate-500")
                                                    )}
                                                    style={sel ? { backgroundColor: d === 'easy' ? '#d1fae5' : d === 'medium' ? '#fef3c7' : '#fee2e2',
                                                                    borderColor: d === 'easy' ? '#6ee7b7' : d === 'medium' ? '#fcd34d' : '#fca5a5',
                                                                    color: d === 'easy' ? '#065f46' : d === 'medium' ? '#92400e' : '#991b1b' } : {}}>
                                                    {d}
                                                </button>
                                            );
                                        })}
                                    </div>
                                </div>
                                {quizForm.mode === 'ai' && (
                                    <div><Label>Questions</Label><Input type="number" min={1} max={50} value={quizForm.num_questions} onChange={e => setQuizForm(f => ({ ...f, num_questions: parseInt(e.target.value) || 1 }))} className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} data-testid="quiz-num-questions" /></div>
                                )}
                                <div>
                                    <Label>Time Limit (min)</Label>
                                    <Input type="number" min={5} max={180} value={quizForm.time_limit_minutes} onChange={e => setQuizForm(f => ({ ...f, time_limit_minutes: parseInt(e.target.value) || 30 }))} className={isDark ? "bg-slate-800 border-slate-600 text-white" : ""} />
                                </div>
                            </div>

                            {/* Manual question builder */}
                            {quizForm.mode === 'manual' && (
                                <div className="space-y-4 pt-2">
                                    <div className="flex items-center justify-between">
                                        <Label className="text-base font-semibold">Questions ({manualQuestions.length})</Label>
                                        <Button variant="outline" size="sm" onClick={addManualQuestion}><Plus className="h-3 w-3 mr-1" />Add</Button>
                                    </div>
                                    {manualQuestions.map((mq, qi) => (
                                        <div key={qi} className={cn("rounded-lg border p-4 space-y-3", isDark ? "border-slate-600 bg-slate-800/50" : "border-slate-200 bg-slate-50")}>
                                            <div className="flex items-center justify-between">
                                                <span className={cn("text-sm font-medium", isDark ? "text-slate-300" : "text-slate-700")}>Question {qi + 1}</span>
                                                {manualQuestions.length > 1 && <Button variant="ghost" size="sm" className="text-red-500 h-7" onClick={() => removeManualQuestion(qi)}><Trash2 className="h-3 w-3" /></Button>}
                                            </div>
                                            <div><Label className="text-xs">Vignette (optional)</Label><Input value={mq.vignette} onChange={e => updateManualQuestion(qi, 'vignette', e.target.value)} placeholder="Clinical scenario..." className={cn("text-sm", isDark ? "bg-slate-700 border-slate-600 text-white" : "")} /></div>
                                            <div><Label className="text-xs">Question Stem</Label><Input value={mq.question_stem} onChange={e => updateManualQuestion(qi, 'question_stem', e.target.value)} placeholder="What is the most likely diagnosis?" className={cn("text-sm", isDark ? "bg-slate-700 border-slate-600 text-white" : "")} /></div>
                                            <div className="grid grid-cols-2 gap-2">
                                                {mq.options.map((opt, oi) => (
                                                    <div key={oi} className="flex items-center gap-2">
                                                        <span className={cn("text-sm font-medium w-5", isDark ? "text-slate-400" : "text-slate-500")}>{opt.letter}.</span>
                                                        <Input value={opt.text} onChange={e => updateManualOption(qi, oi, e.target.value)} placeholder={`Option ${opt.letter}`} className={cn("text-sm", isDark ? "bg-slate-700 border-slate-600 text-white" : "")} />
                                                    </div>
                                                ))}
                                            </div>
                                            <div className="grid grid-cols-2 gap-3">
                                                <div>
                                                    <Label className="text-xs">Correct Answer</Label>
                                                    <select value={mq.correct_answer} onChange={e => updateManualQuestion(qi, 'correct_answer', e.target.value)}
                                                        className={cn("flex h-9 w-full rounded-md border px-3 py-1 text-sm", isDark ? "bg-slate-700 border-slate-600 text-white" : "bg-background border-input")}>
                                                        {['A', 'B', 'C', 'D'].map(l => <option key={l} value={l}>{l}</option>)}
                                                    </select>
                                                </div>
                                                <div><Label className="text-xs">Explanation</Label><Input value={mq.explanation} onChange={e => updateManualQuestion(qi, 'explanation', e.target.value)} placeholder="Why this answer..." className={cn("text-sm", isDark ? "bg-slate-700 border-slate-600 text-white" : "")} /></div>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        <DialogFooter>
                            <Button variant="outline" onClick={() => { setShowCreate(false); resetForm(); }} className={isDark ? "border-slate-600 text-slate-300" : ""}>Cancel</Button>
                            <Button onClick={handleCreateQuiz} disabled={creating} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="quiz-create-btn">
                                {creating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />{quizForm.mode === 'ai' ? 'Generating...' : 'Creating...'}</> : <><Plus className="h-4 w-4 mr-2" />Create Quiz</>}
                            </Button>
                        </DialogFooter>
                    </DialogContent>
                </Dialog>

                {/* Results Dialog */}
                <Dialog open={resultsDialog.open} onOpenChange={(o) => { if (!o) setResultsDialog({ open: false, quizId: null }); }}>
                    <DialogContent className={cn("sm:max-w-2xl max-h-[85vh] overflow-y-auto", isDark && "bg-slate-900 border-slate-700 text-slate-100")} data-testid="quiz-results-dialog">
                        <DialogHeader>
                            <DialogTitle className={cn("flex items-center gap-2", isDark && "text-white")}>
                                <BarChart3 className="h-5 w-5 text-indigo-500" />Quiz Results
                            </DialogTitle>
                        </DialogHeader>
                        {loadingResults ? (
                            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-indigo-500" /></div>
                        ) : results ? (
                            <div className="space-y-4">
                                <div className="grid grid-cols-3 gap-3">
                                    <div className={cn("rounded-lg p-3 text-center", isDark ? "bg-slate-800" : "bg-slate-50")}>
                                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Responses</p>
                                        <p className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-900")}>{results.total_attempts}</p>
                                    </div>
                                    <div className={cn("rounded-lg p-3 text-center", isDark ? "bg-slate-800" : "bg-slate-50")}>
                                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Avg Score</p>
                                        <p className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-900")}>{results.average_score}%</p>
                                    </div>
                                    <div className={cn("rounded-lg p-3 text-center", isDark ? "bg-slate-800" : "bg-slate-50")}>
                                        <p className={cn("text-xs", isDark ? "text-slate-400" : "text-slate-500")}>Pass Rate</p>
                                        <p className={cn("text-xl font-bold", isDark ? "text-white" : "text-slate-900")}>
                                            {results.total_attempts > 0 ? Math.round(results.attempts.filter(a => a.score >= 70).length / results.total_attempts * 100) : 0}%
                                        </p>
                                    </div>
                                </div>

                                {/* Auto-assign button */}
                                {results.total_attempts > 0 && (
                                    <Button
                                        onClick={() => handleAutoAssign(resultsDialog.quizId)}
                                        disabled={autoAssigning === resultsDialog.quizId}
                                        className="w-full bg-red-600 hover:bg-red-700 text-white"
                                        data-testid="auto-assign-btn"
                                    >
                                        {autoAssigning === resultsDialog.quizId ? (
                                            <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Analyzing & Assigning...</>
                                        ) : (
                                            <><Zap className="h-4 w-4 mr-2" />Auto-Assign Study Plans for Weak Areas</>
                                        )}
                                    </Button>
                                )}

                                {results.attempts.length > 0 && (
                                    <>
                                        <h4 className={cn("font-semibold text-sm", isDark ? "text-slate-200" : "text-slate-800")}>Student Scores</h4>
                                        <div className="rounded-lg border overflow-hidden" style={{ borderColor: isDark ? '#334155' : '#e2e8f0' }}>
                                            <Table>
                                                <TableHeader>
                                                    <TableRow className={isDark ? "bg-slate-700/50" : "bg-slate-50"}>
                                                        <TableHead className={isDark ? "text-slate-300" : ""}>Student</TableHead>
                                                        <TableHead className={isDark ? "text-slate-300" : ""}>Score</TableHead>
                                                        <TableHead className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>Correct</TableHead>
                                                        <TableHead className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>Submitted</TableHead>
                                                    </TableRow>
                                                </TableHeader>
                                                <TableBody>
                                                    {results.attempts.map(a => (
                                                        <TableRow key={a.id} className={isDark ? "border-slate-700" : ""}>
                                                            <TableCell>
                                                                <p className={cn("font-medium", isDark ? "text-slate-200" : "")}>{a.student_name}</p>
                                                                <p className={cn("text-xs", isDark ? "text-slate-500" : "text-slate-400")}>{a.student_email}</p>
                                                            </TableCell>
                                                            <TableCell>
                                                                <Badge className={a.score >= 70 ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                                                                    {a.score}%
                                                                </Badge>
                                                            </TableCell>
                                                            <TableCell className={cn("hidden sm:table-cell", isDark ? "text-slate-300" : "")}>{a.correct_count}/{a.total_questions}</TableCell>
                                                            <TableCell className={cn("hidden sm:table-cell text-sm", isDark ? "text-slate-400" : "text-slate-500")}>{a.completed_at ? new Date(a.completed_at).toLocaleDateString() : '-'}</TableCell>
                                                        </TableRow>
                                                    ))}
                                                </TableBody>
                                            </Table>
                                        </div>

                                        <h4 className={cn("font-semibold text-sm pt-2", isDark ? "text-slate-200" : "text-slate-800")}>Question Analysis</h4>
                                        <div className="space-y-2">
                                            {results.question_analysis.map((qa, i) => (
                                                <div key={qa.question_id} className={cn("flex items-center gap-3 rounded-lg p-2", isDark ? "bg-slate-800" : "bg-slate-50")}>
                                                    <span className={cn("text-xs font-mono w-6 text-center shrink-0", isDark ? "text-slate-500" : "text-slate-400")}>Q{i + 1}</span>
                                                    <div className="flex-1 min-w-0">
                                                        <p className={cn("text-sm truncate", isDark ? "text-slate-300" : "text-slate-700")}>{qa.stem}</p>
                                                    </div>
                                                    <Badge className={qa.percent_correct >= 70 ? "bg-emerald-100 text-emerald-700" : qa.percent_correct >= 40 ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}>
                                                        {qa.percent_correct}%
                                                    </Badge>
                                                </div>
                                            ))}
                                        </div>
                                    </>
                                )}
                                {results.attempts.length === 0 && (
                                    <p className={cn("text-center py-8 text-sm", isDark ? "text-slate-400" : "text-slate-500")}>No responses yet.</p>
                                )}
                            </div>
                        ) : null}
                    </DialogContent>
                </Dialog>
                {/* Share Quiz Dialog */}
                <Dialog open={!!shareQuiz} onOpenChange={(o) => { if (!o) setShareQuiz(null); }}>
                    <DialogContent className={cn("sm:max-w-md", isDark && "bg-slate-900 border-slate-700 text-slate-100")} data-testid="quiz-share-dialog">
                        <DialogHeader>
                            <DialogTitle className={cn("flex items-center gap-2", isDark && "text-white")}>
                                <Share2 className="h-5 w-5 text-indigo-500" />Share Quiz
                            </DialogTitle>
                            <DialogDescription className={isDark ? "text-slate-400" : ""}>
                                Share this quiz with your students via link or QR code.
                            </DialogDescription>
                        </DialogHeader>
                        {shareQuiz && (
                            <div className="space-y-5 py-2">
                                {/* Quiz Info */}
                                <div className={cn("rounded-lg p-3 border", isDark ? "bg-slate-800 border-slate-700" : "bg-slate-50 border-slate-200")}>
                                    <p className={cn("font-semibold text-sm", isDark ? "text-white" : "text-slate-900")}>{shareQuiz.title}</p>
                                    <p className={cn("text-xs mt-0.5", isDark ? "text-slate-400" : "text-slate-500")}>
                                        {shareQuiz.subject} &middot; {shareQuiz.num_questions} questions &middot; {shareQuiz.time_limit_minutes} min
                                    </p>
                                </div>

                                {/* Join Code */}
                                <div>
                                    <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-slate-500")}>Join Code</Label>
                                    <div className="flex items-center gap-2">
                                        <code className={cn(
                                            "flex-1 text-center text-2xl font-mono font-bold tracking-[0.3em] py-2 rounded-lg border",
                                            isDark ? "bg-slate-800 border-slate-600 text-indigo-300" : "bg-slate-50 border-slate-200 text-indigo-700"
                                        )} data-testid="share-quiz-code">
                                            {shareQuiz.quiz_code}
                                        </code>
                                        <Button variant="outline" size="sm" onClick={() => copyCode(shareQuiz.quiz_code)}
                                            className={isDark ? "border-slate-600" : ""} data-testid="share-copy-code-btn">
                                            {copiedCode === shareQuiz.quiz_code ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>

                                {/* Quiz Link */}
                                <div>
                                    <Label className={cn("text-xs mb-1.5 block", isDark ? "text-slate-400" : "text-slate-500")}>Direct Link</Label>
                                    <div className="flex items-center gap-2">
                                        <div className={cn(
                                            "flex-1 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm truncate",
                                            isDark ? "bg-slate-800 border-slate-600 text-slate-300" : "bg-slate-50 border-slate-200 text-slate-600"
                                        )} data-testid="share-quiz-link">
                                            <Link2 className="h-3.5 w-3.5 text-indigo-500 flex-shrink-0" />
                                            <span className="truncate">{getQuizUrl(shareQuiz.quiz_code)}</span>
                                        </div>
                                        <Button variant="outline" size="sm" onClick={() => copyQuizLink(shareQuiz.quiz_code)}
                                            className={isDark ? "border-slate-600" : ""} data-testid="share-copy-link-btn">
                                            {copiedLink ? <Check className="h-4 w-4 text-emerald-500" /> : <Copy className="h-4 w-4" />}
                                        </Button>
                                    </div>
                                </div>

                                {/* QR Code */}
                                <div className="flex flex-col items-center">
                                    <Label className={cn("text-xs mb-3 block", isDark ? "text-slate-400" : "text-slate-500")}>QR Code</Label>
                                    <div className={cn(
                                        "p-4 rounded-xl border",
                                        isDark ? "bg-white border-slate-600" : "bg-white border-slate-200 shadow-sm"
                                    )} data-testid="share-qr-code" id="qr-code-container">
                                        <QRCodeSVG
                                            value={getQuizUrl(shareQuiz.quiz_code)}
                                            size={200}
                                            level="M"
                                            includeMargin={false}
                                            bgColor="#ffffff"
                                            fgColor="#1e1b4b"
                                        />
                                    </div>
                                    <Button
                                        variant="outline"
                                        size="sm"
                                        className="mt-3"
                                        data-testid="share-download-qr-btn"
                                        onClick={() => {
                                            const svg = document.querySelector('#qr-code-container svg');
                                            if (!svg) return;
                                            const canvas = document.createElement('canvas');
                                            canvas.width = 400; canvas.height = 400;
                                            const ctx = canvas.getContext('2d');
                                            ctx.fillStyle = '#ffffff';
                                            ctx.fillRect(0, 0, 400, 400);
                                            const img = new Image();
                                            const svgData = new XMLSerializer().serializeToString(svg);
                                            img.onload = () => {
                                                ctx.drawImage(img, 0, 0, 400, 400);
                                                const a = document.createElement('a');
                                                a.download = `quiz-${shareQuiz.quiz_code}-qr.png`;
                                                a.href = canvas.toDataURL('image/png');
                                                a.click();
                                            };
                                            img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(svgData)));
                                        }}
                                    >
                                        <ExternalLink className="h-3.5 w-3.5 mr-1.5" /> Download QR Code
                                    </Button>
                                    <p className={cn("text-xs mt-2", isDark ? "text-slate-500" : "text-slate-400")}>
                                        Students can scan to join instantly
                                    </p>
                                </div>
                            </div>
                        )}
                    </DialogContent>
                </Dialog>
            </div>
        </Layout>
    );
}
